/**
 * Vue courante du détail d'une campagne : onglet actif, section du test, cible
 * de l'onglet Référentiels, et candidat ouvert en panneau.
 *
 * Existe pour que le PARENT (le wrapper de queries de chaque portail) sache ce
 * que la page va afficher, et puisse ne charger que ça. Sans ce hook partagé,
 * l'état vivait dans `CampagneDetailView` et le parent chargeait à l'aveugle
 * toutes les passations de la campagne.
 *
 * Tout vit dans l'URL (`?onglet=pami&section=matching&candidat=…`) : coller le
 * lien reproduit la vue, panneau compris, et parent comme enfant lisent la même
 * source réactive.
 */
import type { TestCampagne } from "@repo/results";
import { useFiltresUrl } from "@repo/ui";
import {
  estTest,
  SANS_SCORE_GLOBAL,
  SECTIONS_DU_TEST,
  type CibleReferentiel,
  type OngletCampagne,
  type SectionTest,
} from "./ongletsCampagne";

/**
 * Référence stable : recréée à chaque rendu, elle ferait boucler les mémos de
 * `useFiltresUrl`. `test` n'est plus écrit : il n'est lu que pour traduire les
 * liens d'avant la navigation par test (`?onglet=comparaison&test=pami`).
 * `fiche` et `vue` appartiennent à la fiche candidat ; ils sont déclarés ici
 * pour être effacés quand le panneau se ferme.
 */
const DEFAUTS: {
  onglet: string;
  section: string;
  test: string;
  cible: string;
  candidat: string;
  fiche: string;
  vue: string;
} = {
  onglet: "candidats",
  section: "resultats",
  test: "",
  cible: "",
  candidat: "",
  fiche: "ensemble",
  vue: "resultats",
};

/** Anciens onglets par fonction → section du test. */
const SECTION_HERITEE: Record<string, SectionTest> = {
  comparaison: "resultats",
  matching: "matching",
};

/**
 * Sections et onglets d'avant le regroupement : Référentiel / Référents
 * vivaient sous l'onglet de leur test, Parcours et Synthèse étaient deux
 * tables des mêmes candidats.
 */
const VERS_REFERENTIELS = new Set(["referentiel", "referents"]);
const VERS_CANDIDATS = new Set(["parcours", "synthese"]);

const CIBLE_DE: Partial<Record<string, CibleReferentiel>> = {
  pami: "pami",
  fitculturel: "pc-fit",
};

export type VueCampagne = {
  /**
   * Onglet demandé par l'URL. Non validé contre la campagne : la vue retombe
   * sur Candidats si la campagne ne fait pas passer ce test.
   */
  onglet: OngletCampagne;
  /** Section du test, bornée aux sections que ce test peut porter. */
  section: SectionTest;
  /** Cible de l'onglet Référentiels. `undefined` = la première de la campagne. */
  cible: CibleReferentiel | undefined;
  /** Candidat ouvert en panneau. */
  candidat: string | undefined;
  choisirOnglet: (onglet: OngletCampagne) => void;
  choisirSection: (section: SectionTest) => void;
  /** Ouvre l'onglet Référentiels sur une cible. */
  ouvrirReferentiel: (cible: CibleReferentiel) => void;
  /**
   * Lien qui ouvre un candidat en panneau, l'URL courante conservée. Avec
   * `ouverture`, la fiche s'ouvre sur cette épreuve (et cette vue).
   */
  hrefCandidat: (
    parcoursId: string,
    ouverture?: { fiche: string; vue?: "resultats" | "matching" },
  ) => string;
  /** Lien pleine page de la fiche ouverte, sur le même onglet. */
  hrefPleinePage: (base: string) => string;
  ouvrirCandidat: (parcoursId: string) => void;
  fermerCandidat: () => void;
  /**
   * Les passations de ce test sont-elles nécessaires à ce qui est affiché ?
   *
   * La table Candidats montre un score par test (sauf In-Basket et COPSOQ) ;
   * les sections Résultats / Matching d'un test, ses propres passations.
   * Référentiels n'en a pas besoin.
   */
  chargerEvaluations: (test: TestCampagne) => boolean;
};

function lireOnglet(params: typeof DEFAUTS): OngletCampagne {
  const { onglet, test, section } = params;
  if (VERS_REFERENTIELS.has(onglet)) return "referentiels";
  if (VERS_CANDIDATS.has(onglet)) return "candidats";
  if (onglet in SECTION_HERITEE) {
    if (test === "synthese") return "candidats";
    return estTest(test) ? test : "candidats";
  }
  if (estTest(onglet) && VERS_REFERENTIELS.has(section)) return "referentiels";
  if (onglet === "referentiels" || estTest(onglet)) return onglet;
  return "candidats";
}

function lireCible(params: typeof DEFAUTS): CibleReferentiel | undefined {
  if (params.cible === "pami" || params.cible === "pc-fit") return params.cible;
  // Lien hérité : la cible était portée par l'onglet de test.
  return CIBLE_DE[params.onglet] ?? CIBLE_DE[params.test];
}

export function useVueCampagne(): VueCampagne {
  const [params, setParams] = useFiltresUrl(DEFAUTS);

  const onglet = lireOnglet(params);
  const demandee = (SECTION_HERITEE[params.onglet] ?? params.section) as SectionTest;
  const sectionsPossibles = estTest(onglet) ? SECTIONS_DU_TEST[onglet] : [];
  const section = sectionsPossibles.includes(demandee) ? demandee : "resultats";
  const cible = onglet === "referentiels" ? lireCible(params) : undefined;
  const candidat = params.candidat || undefined;

  // Changer de test garde la section si le nouveau test la porte : passer du
  // matching PAMI au matching fit culturel compare la même chose. Sinon, on
  // repart sur ses Résultats.
  const choisirOnglet = (valeur: OngletCampagne) => {
    const garde = estTest(valeur) && SECTIONS_DU_TEST[valeur].includes(section);
    setParams({
      onglet: valeur,
      section: garde ? section : "resultats",
      test: "",
      cible: "",
    });
  };

  const chargerEvaluations = (test: TestCampagne) =>
    (onglet === "candidats" && !SANS_SCORE_GLOBAL.includes(test)) ||
    onglet === test;

  return {
    onglet,
    section,
    cible,
    candidat,
    choisirOnglet,
    // `onglet` réécrit aussi : sur un lien hérité, l'URL porte encore l'ancien
    // onglet par fonction, qui primerait sur la section choisie.
    choisirSection: (valeur) => setParams({ onglet, section: valeur, test: "" }),
    ouvrirReferentiel: (valeur) =>
      setParams({ onglet: "referentiels", section: "resultats", test: "", cible: valeur }),
    hrefCandidat: (parcoursId, ouverture) => {
      const suivant = new URLSearchParams(window.location.search);
      suivant.set("candidat", parcoursId);
      if (ouverture) {
        suivant.set("fiche", ouverture.fiche);
        if (ouverture.vue && ouverture.vue !== DEFAUTS.vue) {
          suivant.set("vue", ouverture.vue);
        } else {
          suivant.delete("vue");
        }
      }
      return `?${suivant.toString()}`;
    },
    hrefPleinePage: (base) => {
      const fiche = new URLSearchParams();
      if (params.fiche !== DEFAUTS.fiche) fiche.set("fiche", params.fiche);
      if (params.vue !== DEFAUTS.vue) fiche.set("vue", params.vue);
      const suite = fiche.toString();
      if (!suite) return base;
      return `${base}${base.includes("?") ? "&" : "?"}${suite}`;
    },
    ouvrirCandidat: (parcoursId) => setParams({ candidat: parcoursId }),
    // La fiche garde son onglet d'un candidat à l'autre (comparer le PAMI de
    // deux candidats à la suite) ; fermer le panneau l'oublie.
    fermerCandidat: () =>
      setParams({ candidat: "", fiche: DEFAUTS.fiche, vue: DEFAUTS.vue }),
    chargerEvaluations,
  };
}
